import { appendQualitySlide } from './reportQualityExport';
// Generate PowerPoint report for Sustainability/ESG Maturity Assessment
import pptxgen from 'pptxgenjs';

export const generateSustainabilityPptx = (results, fullResults) => {
  const pptx = new pptxgen();
  
  // Set presentation properties
  pptx.author = 'SAMANVAI';
  pptx.title = `ESG Maturity Assessment - ${results.company_name}`;
  pptx.subject = 'Sustainability Assessment Report';
  pptx.company = 'SAMANVAI';
  
  // Color scheme - Sustainability theme (Emerald/Teal)
  const colors = {
    primary: '10B981',    // Emerald
    secondary: '14B8A6',  // Teal
    accent: '0D9488',     // Teal dark
    dark: '064E3B',       // Emerald 900
    darkGray: '1F2937',
    mediumGray: '4B5563',
    lightGray: 'F3F4F6',
    white: 'FFFFFF',
    red: 'EF4444',
    orange: 'F97316',
    amber: 'F59E0B',
    green: '22C55E'
  };
  
  const getScoreColor = (score) => {
    if (score < 1.5) return colors.red;
    if (score < 2.5) return colors.orange;
    if (score < 3.5) return colors.amber;
    return colors.primary;
  };

  const data = fullResults || results;
  const benchmark = data.industry_benchmark || { avg: 2.4, top: 4.2 };
  const overallScore = data.overall_score || results.overall_score || 2.0;
  const framework = data.regional_framework?.name || 'Global Standards';
  const dimensions = data.dimension_scores || results.dimension_scores || [];

  // Helper function to find matching analysis for a dimension
  const findDimensionAnalysis = (dimName, dimId) => {
    const aiAnalysis = data.ai_analysis?.dimension_analysis || data.dimension_analysis || [];
    if (!Array.isArray(aiAnalysis) || aiAnalysis.length === 0) return null;
    
    return aiAnalysis.find(a => {
      if (!a) return false;
      if (dimId && (a.dimension_id === dimId || a.id === dimId)) return true;
      if (a.dimension === dimName || a.name === dimName) return true;
      const searchTerm = dimName.toLowerCase().split(' ')[0];
      const analysisName = (a.dimension || a.name || '').toLowerCase();
      return analysisName.includes(searchTerm) || searchTerm.includes(analysisName.split(' ')[0]);
    });
  };

  // ESG-specific recommendations based on dimension and score
  const getESGSpecificRecommendation = (dimName, score, analysis) => {
    if (analysis) {
      const recs = analysis.quick_wins || analysis.recommendations || analysis.improvement_areas || [];
      if (Array.isArray(recs) && recs.length > 0) {
        return typeof recs[0] === 'string' ? recs[0] : recs[0].recommendation || recs[0].action || String(recs[0]);
      }
      if (analysis.current_state) {
        return analysis.current_state.substring(0, 150);
      }
    }
    
    const dimLower = dimName.toLowerCase();
    if (score < 2) {
      if (dimLower.includes('carbon') || dimLower.includes('emission') || dimLower.includes('climate')) 
        return 'URGENT: Establish GHG emissions baseline and implement Scope 1 & 2 carbon accounting';
      if (dimLower.includes('energy')) 
        return 'CRITICAL: Deploy energy monitoring systems and set science-based reduction targets';
      if (dimLower.includes('water')) 
        return 'PRIORITY: Implement water consumption tracking and identify high-usage operations';
      if (dimLower.includes('waste') || dimLower.includes('circular')) 
        return 'IMMEDIATE: Conduct waste audit and implement waste segregation at all facilities';
      if (dimLower.includes('supply') || dimLower.includes('chain')) 
        return 'URGENT: Map Tier 1 suppliers and begin ESG due diligence questionnaires';
      if (dimLower.includes('social') || dimLower.includes('diversity') || dimLower.includes('dei')) 
        return 'CRITICAL: Establish DEI baseline metrics and implement inclusive hiring practices';
      if (dimLower.includes('governance') || dimLower.includes('board')) 
        return 'FOUNDATIONAL: Establish ESG committee at board level with clear charter';
      if (dimLower.includes('stakeholder')) 
        return 'PRIORITY: Identify key stakeholders and establish formal engagement mechanisms';
    } else if (score < 3) {
      if (dimLower.includes('carbon') || dimLower.includes('emission') || dimLower.includes('climate')) 
        return 'Expand to Scope 3 emissions tracking and develop decarbonization roadmap';
      if (dimLower.includes('energy')) 
        return 'Implement renewable energy procurement strategy and efficiency improvements';
      if (dimLower.includes('water')) 
        return 'Deploy water recycling systems and set water intensity reduction targets';
      if (dimLower.includes('waste') || dimLower.includes('circular')) 
        return 'Implement circular economy principles and achieve zero-waste-to-landfill targets';
      if (dimLower.includes('supply') || dimLower.includes('chain')) 
        return 'Extend ESG assessments to Tier 2 suppliers and implement corrective action programs';
      if (dimLower.includes('social') || dimLower.includes('diversity') || dimLower.includes('dei')) 
        return 'Scale DEI programs across all levels and implement pay equity analysis';
      if (dimLower.includes('governance') || dimLower.includes('board')) 
        return 'Link executive compensation to ESG KPIs and enhance disclosure practices';
      if (dimLower.includes('stakeholder')) 
        return 'Implement materiality assessment and integrate stakeholder feedback into strategy';
    } else {
      if (dimLower.includes('carbon') || dimLower.includes('emission') || dimLower.includes('climate')) 
        return 'Achieve verified net-zero pathway and implement internal carbon pricing';
      if (dimLower.includes('energy')) 
        return 'Reach 100% renewable energy and implement smart grid integration';
      if (dimLower.includes('water')) 
        return 'Achieve water positive operations and lead watershed restoration initiatives';
      if (dimLower.includes('waste') || dimLower.includes('circular')) 
        return 'Pioneer closed-loop manufacturing and lead industry circularity standards';
      if (dimLower.includes('supply') || dimLower.includes('chain')) 
        return 'Achieve full supply chain transparency with real-time ESG monitoring';
      if (dimLower.includes('social') || dimLower.includes('diversity') || dimLower.includes('dei')) 
        return 'Lead industry in DEI representation and publish transparent impact reports';
      if (dimLower.includes('governance') || dimLower.includes('board')) 
        return 'Achieve ESG leadership recognition and integrate into all governance processes';
      if (dimLower.includes('stakeholder')) 
        return 'Pioneer stakeholder capitalism model and lead industry collaboration';
    }
    return `Strengthen ${dimName.toLowerCase()} practices with targeted improvements based on ESG best practices`;
  };

  // Score-based roadmap phases
  const getESGRoadmap = (score) => {
    if (score < 2) {
      return {
        phase_1: {
          name: 'Assessment & Baseline',
          focus_areas: ['GHG emissions inventory', 'ESG policy development', 'Stakeholder mapping'],
          key_initiatives: ['Complete carbon footprint baseline', 'Establish ESG governance', 'Develop materiality matrix', 'Begin supplier ESG screening'],
          investment_range: '$50K - $150K'
        },
        phase_2: {
          name: 'Foundation Building',
          focus_areas: ['Data collection systems', 'Policy implementation', 'Training rollout'],
          key_initiatives: ['Deploy ESG data platform', 'Train leadership on ESG', 'Implement quick-win reductions', 'Publish first ESG report'],
          investment_range: '$100K - $250K'
        },
        phase_3: {
          name: 'Program Scale-up',
          focus_areas: ['Target setting', 'Supplier engagement', 'Process improvements'],
          key_initiatives: ['Set science-based targets', 'Launch supplier ESG program', 'Implement energy efficiency', 'Expand social programs'],
          investment_range: '$150K - $350K'
        },
        phase_4: {
          name: 'Integration',
          focus_areas: ['Strategy integration', 'External assurance', 'Stakeholder reporting'],
          key_initiatives: ['Integrate ESG into business strategy', 'Obtain third-party verification', 'Achieve compliance milestones', 'Apply for ESG ratings'],
          investment_range: '$100K - $200K'
        }
      };
    } else if (score < 3) {
      return {
        phase_1: {
          name: 'Acceleration',
          focus_areas: ['Scope 3 emissions', 'Advanced metrics', 'Supply chain depth'],
          key_initiatives: ['Complete Scope 3 inventory', 'Implement TCFD reporting', 'Deepen supplier due diligence', 'Enhance biodiversity programs'],
          investment_range: '$100K - $250K'
        },
        phase_2: {
          name: 'Standardization',
          focus_areas: ['Process automation', 'Global alignment', 'Impact measurement'],
          key_initiatives: ['Automate ESG data collection', 'Standardize global ESG programs', 'Implement impact valuation', 'Launch circular initiatives'],
          investment_range: '$150K - $350K'
        },
        phase_3: {
          name: 'Innovation',
          focus_areas: ['Clean technology', 'Social innovation', 'Governance excellence'],
          key_initiatives: ['Invest in clean tech solutions', 'Launch community impact programs', 'Achieve board diversity targets', 'Implement green financing'],
          investment_range: '$200K - $450K'
        },
        phase_4: {
          name: 'Leadership',
          focus_areas: ['Industry leadership', 'Advocacy', 'Continuous improvement'],
          key_initiatives: ['Lead industry ESG initiatives', 'Engage in climate advocacy', 'Achieve top-quartile ratings', 'Pioneer new ESG standards'],
          investment_range: '$150K - $300K'
        }
      };
    }
    return {
      phase_1: {
        name: 'Excellence',
        focus_areas: ['Net-zero pathway', 'Thought leadership', 'Systemic change'],
        key_initiatives: ['Implement verified net-zero plan', 'Lead industry coalitions', 'Pioneer regenerative practices', 'Drive policy engagement'],
        investment_range: '$150K - $300K'
      },
      phase_2: {
        name: 'Transformation',
        focus_areas: ['Business model innovation', 'Value chain transformation', 'Impact scaling'],
        key_initiatives: ['Transition to circular model', 'Transform supplier ecosystem', 'Scale social enterprises', 'Implement nature-positive strategy'],
        investment_range: '$250K - $500K'
      },
      phase_3: {
        name: 'Systemic Impact',
        focus_areas: ['Ecosystem building', 'Policy shaping', 'Knowledge sharing'],
        key_initiatives: ['Build sustainable ecosystem', 'Shape regulatory frameworks', 'Open-source ESG innovations', 'Lead just transition initiatives'],
        investment_range: '$300K - $600K'
      },
      phase_4: {
        name: 'Legacy Building',
        focus_areas: ['Long-term resilience', 'Generational impact', 'Industry transformation'],
        key_initiatives: ['Ensure multi-generational impact', 'Transform industry standards', 'Create sustainable markets', 'Build lasting partnerships'],
        investment_range: '$200K - $400K'
      }
    };
  };

  // ============== Slide 1: Title Slide ==============
  const slide1 = pptx.addSlide();
  slide1.background = { color: colors.dark };
  
  slide1.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slide1.addText('ESG / Sustainability Assessment', {
    x: 0.5, y: 2, w: 7, h: 1,
    fontSize: 40, bold: true, color: colors.white
  });
  
  slide1.addText('Executive Report', {
    x: 0.5, y: 2.9, w: 5, h: 0.5,
    fontSize: 20, color: colors.secondary
  });
  
  slide1.addText(data.company_name || results.company_name, {
    x: 0.5, y: 3.6, w: 6, h: 0.6,
    fontSize: 28, color: colors.secondary
  });
  
  slide1.addText(`${data.industry || results.industry} | ${data.region || results.region}`, {
    x: 0.5, y: 4.3, w: 6, h: 0.4,
    fontSize: 12, color: 'A0AEC0'
  });
  
  slide1.addText(`Framework: ${framework}`, {
    x: 0.5, y: 4.7, w: 6, h: 0.3,
    fontSize: 11, color: '718096'
  });
  
  // Overall Score Badge
  slide1.addShape('rect', {
    x: 7.3, y: 1.6, w: 2.2, h: 3,
    fill: { color: getScoreColor(overallScore) },
    line: { color: colors.white, width: 2 }
  });
  slide1.addText(overallScore.toFixed(1), {
    x: 7.3, y: 1.9, w: 2.2, h: 1.2,
    fontSize: 52, bold: true, color: colors.white, align: 'center'
  });
  slide1.addText('ESG Score', {
    x: 7.3, y: 3.0, w: 2.2, h: 0.3,
    fontSize: 11, color: colors.white, align: 'center'
  });
  slide1.addText(data.maturity_level?.name || 'Developing', {
    x: 7.3, y: 3.4, w: 2.2, h: 0.5,
    fontSize: 10, bold: true, color: colors.white, align: 'center', wrap: true
  });

  // ============== Slide 2: Executive Summary ==============
  const slide2 = pptx.addSlide();
  
  slide2.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide2.addText('Executive Summary', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Key Stats
  const stats = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore) },
    { label: 'Industry Avg', value: String(benchmark.avg), color: colors.mediumGray },
    { label: 'Top Performers', value: String(benchmark.top), color: colors.primary },
    { label: 'Gap to Avg', value: (overallScore - benchmark.avg).toFixed(2), color: overallScore >= benchmark.avg ? colors.green : colors.red }
  ];
  
  stats.forEach((stat, idx) => {
    const x = 0.5 + idx * 2.4;
    slide2.addShape('rect', {
      x: x, y: 1.3, w: 2.2, h: 1.1,
      fill: { color: colors.lightGray }
    });
    slide2.addText(stat.label, {
      x: x, y: 1.4, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });
    slide2.addText(stat.value, {
      x: x, y: 1.65, w: 2.2, h: 0.6,
      fontSize: 24, bold: true, color: stat.color, align: 'center'
    });
  });
  
  // Summary Text
  let summaryText = '';
  const execSummary = data.ai_analysis?.executive_summary;
  
  if (typeof execSummary === 'string' && execSummary.length > 50) {
    summaryText = execSummary.substring(0, 1500);
  } else {
    summaryText = `${data.company_name || results.company_name} has achieved an ESG Maturity score of ${overallScore.toFixed(1)}/5.0, placing the organization at the ${data.maturity_level?.name || 'Developing'} level. This assessment is aligned with ${framework} requirements.

The assessment reveals opportunities for improvement across environmental, social, and governance dimensions. Key focus areas include climate disclosure, supply chain due diligence, and stakeholder engagement.

Based on the 12-month transformation roadmap, the organization can improve its ESG maturity while meeting regulatory requirements and stakeholder expectations.`;
  }
  
  slide2.addText(summaryText, {
    x: 0.5, y: 2.6, w: 9, h: 2.5,
    fontSize: 11, color: colors.darkGray, valign: 'top'
  });

  // ============== Slide 3: Dimension Scores ==============
  const slide3 = pptx.addSlide();
  
  slide3.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide3.addText('ESG Dimension Analysis', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  dimensions.forEach((dim, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const x = 0.5 + col * 4.8;
    const y = 1.2 + row * 0.9;
    
    const dimScore = dim.score || 2.0;
    
    slide3.addShape('rect', {
      x: x, y: y, w: 4.5, h: 0.8,
      fill: { color: colors.lightGray }
    });
    
    // Score
    slide3.addShape('rect', {
      x: x, y: y, w: 0.8, h: 0.8,
      fill: { color: getScoreColor(dimScore) }
    });
    slide3.addText(dimScore.toFixed(1), {
      x: x, y: y + 0.2, w: 0.8, h: 0.4,
      fontSize: 14, bold: true, color: colors.white, align: 'center'
    });
    
    // Name and status
    slide3.addText(dim.dimension_name || `Dimension ${idx + 1}`, {
      x: x + 0.9, y: y + 0.1, w: 2.8, h: 0.4,
      fontSize: 10, bold: true, color: colors.darkGray
    });
    
    const gap = dimScore - benchmark.avg;
    const status = gap < -0.3 ? 'Below Avg' : gap < 0.3 ? 'At Par' : 'Above Avg';
    slide3.addText(status, {
      x: x + 0.9, y: y + 0.45, w: 2, h: 0.3,
      fontSize: 8, color: gap < -0.3 ? colors.red : gap < 0.3 ? colors.amber : colors.green
    });
  });

  // ============== Slide 3.5: Detailed Dimension Analysis ==============
  const slide3b = pptx.addSlide();
  
  slide3b.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.accent }
  });
  slide3b.addText('Detailed Dimension Analysis', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const firstFiveDims = dimensions.slice(0, 5);
  firstFiveDims.forEach((dim, idx) => {
    const y = 1.2 + idx * 1.0;
    const score = dim.score || 0;
    const dimName = dim.dimension_name || dim.name || `Dimension ${idx + 1}`;
    const dimId = dim.dimension_id || dim.id;
    
    const analysis = findDimensionAnalysis(dimName, dimId);
    
    // Dimension header
    slide3b.addText(`${dimName}: ${score.toFixed(1)}/5.0`, {
      x: 0.5, y: y, w: 4.5, h: 0.35,
      fontSize: 11, bold: true, color: colors.darkGray
    });
    
    // Score bar
    slide3b.addShape('rect', {
      x: 5.5, y: y + 0.05, w: 4, h: 0.25,
      fill: { color: colors.lightGray }
    });
    slide3b.addShape('rect', {
      x: 5.5, y: y + 0.05, w: (score / 5) * 4, h: 0.25,
      fill: { color: getScoreColor(score) }
    });
    
    // Key recommendation - specific to ESG
    const keyRec = getESGSpecificRecommendation(dimName, score, analysis);
    
    slide3b.addText(`→ ${keyRec}`, {
      x: 0.5, y: y + 0.4, w: 9, h: 0.5,
      fontSize: 9, color: colors.mediumGray, wrap: true
    });
  });

  // ============== Slide 4: Areas of Concern ==============
  const slide4 = pptx.addSlide();
  
  slide4.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.red }
  });
  slide4.addText('Areas of Concern', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const concerns = data.ai_analysis?.areas_of_concern || {};
  let yPos = 1.3;
  
  // Generate default critical concerns based on lowest scoring dimensions
  const getDefaultCriticalAreas = () => {
    if (!dimensions || dimensions.length === 0) return [];
    const sortedDims = [...dimensions].sort((a, b) => (a.score || 0) - (b.score || 0));
    return sortedDims.slice(0, 3).map(dim => {
      const name = dim.dimension_name || dim.name;
      const score = dim.score || 0;
      if (score < 1.5) return `CRITICAL: ${name} requires immediate attention - establish foundational ESG practices`;
      if (score < 2.5) return `HIGH PRIORITY: ${name} needs focused improvement to meet regulatory expectations`;
      return `MONITOR: ${name} should be strengthened to achieve ESG leadership`;
    });
  };
  
  const criticalItems = concerns.critical?.length > 0 ? concerns.critical : getDefaultCriticalAreas();
  
  if (criticalItems.length > 0) {
    slide4.addText('🔴 CRITICAL - Immediate Action Required', {
      x: 0.5, y: yPos, w: 9, h: 0.4,
      fontSize: 12, bold: true, color: colors.red
    });
    yPos += 0.5;
    criticalItems.slice(0, 3).forEach((item) => {
      const itemText = typeof item === 'string' ? item : item.description || item.area || String(item);
      slide4.addText(`• ${itemText}`, {
        x: 0.7, y: yPos, w: 8.5, h: 0.4,
        fontSize: 10, color: colors.darkGray
      });
      yPos += 0.4;
    });
    yPos += 0.3;
  }
  
  // Default high priority based on score
  const defaultHighPriority = overallScore < 2 
    ? ['Establish GHG emissions baseline and reporting', 'Develop ESG governance framework with board oversight', 'Begin supplier ESG due diligence program']
    : overallScore < 3
    ? ['Expand Scope 3 emissions tracking and reduction', 'Implement science-based targets (SBTi)', 'Strengthen social impact measurement']
    : ['Pursue net-zero verification', 'Lead industry sustainability coalitions', 'Pioneer circular economy initiatives'];
  
  const highItems = concerns.high_priority?.length > 0 ? concerns.high_priority : defaultHighPriority;
  
  if (highItems.length > 0) {
    slide4.addText('🟠 HIGH PRIORITY - Action within 3 months', {
      x: 0.5, y: yPos, w: 9, h: 0.4,
      fontSize: 12, bold: true, color: colors.orange
    });
    yPos += 0.5;
    highItems.slice(0, 3).forEach((item) => {
      const itemText = typeof item === 'string' ? item : item.description || item.area || String(item);
      slide4.addText(`• ${itemText}`, {
        x: 0.7, y: yPos, w: 8.5, h: 0.4,
        fontSize: 10, color: colors.darkGray
      });
      yPos += 0.4;
    });
  }

  // ============== Slide 5: 12-Month Roadmap ==============
  const slide5 = pptx.addSlide();
  
  slide5.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide5.addText('12-Month Transformation Roadmap', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const aiRoadmap = data.ai_analysis?.strategic_roadmap || {};
  const defaultRoadmap = getESGRoadmap(overallScore);
  
  const phases = [
    { key: 'phase_1', months: 'Months 1-3', color: colors.primary },
    { key: 'phase_2', months: 'Months 4-6', color: colors.secondary },
    { key: 'phase_3', months: 'Months 7-9', color: colors.accent },
    { key: 'phase_4', months: 'Months 10-12', color: colors.dark }
  ];
  
  phases.forEach((phase, idx) => {
    const x = 0.5 + idx * 2.4;
    const phaseData = aiRoadmap[phase.key] || defaultRoadmap[phase.key] || {};
    
    // Phase header
    slide5.addShape('rect', {
      x: x, y: 1.2, w: 2.2, h: 0.6,
      fill: { color: phase.color }
    });
    slide5.addText(phaseData.name || `Phase ${idx + 1}`, {
      x: x, y: 1.25, w: 2.2, h: 0.3,
      fontSize: 10, bold: true, color: colors.white, align: 'center'
    });
    slide5.addText(phase.months, {
      x: x, y: 1.5, w: 2.2, h: 0.25,
      fontSize: 8, color: colors.white, align: 'center'
    });
    
    // Content box
    slide5.addShape('rect', {
      x: x, y: 1.85, w: 2.2, h: 3,
      fill: { color: colors.lightGray }
    });
    
    let contentY = 1.95;
    
    // Focus areas
    const focusAreas = phaseData.focus_areas || [];
    slide5.addText('Focus:', {
      x: x + 0.1, y: contentY, w: 2, h: 0.25,
      fontSize: 8, bold: true, color: phase.color
    });
    contentY += 0.25;
    
    focusAreas.slice(0, 3).forEach(area => {
      const areaText = typeof area === 'string' ? area : (area.area || area.name || String(area));
      slide5.addText(`• ${areaText}`, {
        x: x + 0.1, y: contentY, w: 2, h: 0.35,
        fontSize: 7, color: colors.darkGray, wrap: true
      });
      contentY += 0.35;
    });
    
    contentY += 0.1;
    
    // Key initiatives
    const initiatives = phaseData.key_initiatives || [];
    if (initiatives.length > 0) {
      slide5.addText('Initiatives:', {
        x: x + 0.1, y: contentY, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: phase.color
      });
      contentY += 0.25;
      
      initiatives.slice(0, 3).forEach(init => {
        const initText = typeof init === 'string' ? init : (init.name || init.initiative || String(init));
        const truncatedText = initText.length > 40 ? initText.substring(0, 37) + '...' : initText;
        slide5.addText(`• ${truncatedText}`, {
          x: x + 0.1, y: contentY, w: 2, h: 0.35,
          fontSize: 6.5, color: colors.mediumGray, wrap: true
        });
        contentY += 0.35;
      });
    }
    
    // Investment
    if (phaseData.investment_range) {
      slide5.addShape('rect', {
        x: x + 0.1, y: 4.45, w: 2, h: 0.3,
        fill: { color: phase.color }
      });
      slide5.addText(phaseData.investment_range, {
        x: x + 0.1, y: 4.48, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: colors.white, align: 'center'
      });
    }
    
    // Arrow between phases
    if (idx < 3) {
      slide5.addText('→', {
        x: x + 2.1, y: 1.4, w: 0.4, h: 0.4,
        fontSize: 18, color: colors.mediumGray, align: 'center'
      });
    }
  });

  // ============== Slide 6: ROI Analysis ==============
  const slide6 = pptx.addSlide();
  
  slide6.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide6.addText('ROI Analysis & Business Case', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const roi = data.ai_analysis?.roi_analysis || {};
  
  // Investment & ROI
  slide6.addShape('rect', {
    x: 0.5, y: 1.3, w: 4.5, h: 1.5,
    fill: { color: colors.lightGray }
  });
  slide6.addText('Investment & Returns', {
    x: 0.6, y: 1.4, w: 4.3, h: 0.4,
    fontSize: 12, bold: true, color: colors.primary
  });
  slide6.addText(`Total Investment: ${roi.total_investment || 'Not available'}\nExpected ROI: ${roi.expected_roi || 'Not available'}`, {
    x: 0.6, y: 1.8, w: 4.3, h: 0.8,
    fontSize: 10, color: colors.darkGray
  });
  
  // Cost Savings
  slide6.addShape('rect', {
    x: 5.2, y: 1.3, w: 4.5, h: 1.5,
    fill: { color: colors.lightGray }
  });
  slide6.addText('Cost Savings', {
    x: 5.3, y: 1.4, w: 4.3, h: 0.4,
    fontSize: 12, bold: true, color: colors.green
  });
  const costSavings = roi.cost_savings || ['Energy efficiency improvements', 'Waste reduction'];
  slide6.addText(costSavings.slice(0, 3).map(s => `• ${s}`).join('\n'), {
    x: 5.3, y: 1.8, w: 4.3, h: 0.8,
    fontSize: 9, color: colors.darkGray
  });
  
  // Risk Mitigation
  slide6.addShape('rect', {
    x: 0.5, y: 3, w: 9, h: 1.2,
    fill: { color: colors.lightGray }
  });
  slide6.addText('Risk Mitigation Value', {
    x: 0.6, y: 3.1, w: 8.8, h: 0.4,
    fontSize: 12, bold: true, color: colors.red
  });
  slide6.addText(roi.risk_mitigation_value || 'Reduced regulatory fines, reputational protection, improved access to sustainable finance', {
    x: 0.6, y: 3.5, w: 8.8, h: 0.6,
    fontSize: 10, color: colors.darkGray
  });

  // ============== Slide 7: Use Cases ==============
  const slide7 = pptx.addSlide();
  
  slide7.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.accent }
  });
  slide7.addText('Recommended Use Cases', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const useCases = data.ai_analysis?.use_cases || [
    { title: 'Scope 3 Emissions Measurement', description: 'Implement supplier emissions tracking', impact: 'High', investment: '$50-100K' },
    { title: 'ESG Data Platform', description: 'Centralize ESG data collection and reporting', impact: 'High', investment: '$100-200K' }
  ];
  
  useCases.slice(0, 4).forEach((uc, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const x = 0.5 + col * 4.8;
    const y = 1.3 + row * 2;
    
    slide7.addShape('rect', {
      x: x, y: y, w: 4.5, h: 1.8,
      fill: { color: colors.lightGray }
    });
    
    slide7.addText(uc.title || `Use Case ${idx + 1}`, {
      x: x + 0.1, y: y + 0.1, w: 4.3, h: 0.4,
      fontSize: 11, bold: true, color: colors.primary
    });
    slide7.addText(uc.description || '', {
      x: x + 0.1, y: y + 0.5, w: 4.3, h: 0.6,
      fontSize: 9, color: colors.darkGray
    });
    slide7.addText(`Impact: ${uc.impact || 'High'} | Investment: ${uc.investment || 'TBD'}`, {
      x: x + 0.1, y: y + 1.3, w: 4.3, h: 0.3,
      fontSize: 8, color: colors.mediumGray
    });
  });

  // ============== Slide 8: Regulatory Compliance ==============
  const slide8 = pptx.addSlide();
  
  slide8.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.dark }
  });
  slide8.addText(`Regulatory Framework: ${framework}`, {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 26, bold: true, color: colors.white
  });
  
  const compliance = data.ai_analysis?.regulatory_compliance || {};
  
  slide8.addText(`Compliance Status: ${compliance.compliance_status || 'Partial'}`, {
    x: 0.5, y: 1.3, w: 9, h: 0.4,
    fontSize: 14, bold: true, color: compliance.compliance_status === 'compliant' ? colors.green : colors.amber
  });
  
  // Gaps
  slide8.addText('Compliance Gaps:', {
    x: 0.5, y: 1.9, w: 9, h: 0.4,
    fontSize: 12, bold: true, color: colors.red
  });
  const gaps = compliance.gaps || ['Climate risk disclosure', 'Scope 3 emissions', 'Supply chain due diligence'];
  slide8.addText(gaps.slice(0, 4).map(g => `• ${g}`).join('\n'), {
    x: 0.6, y: 2.3, w: 8.8, h: 1,
    fontSize: 10, color: colors.darkGray
  });
  
  // Actions
  slide8.addText('Required Actions:', {
    x: 0.5, y: 3.5, w: 9, h: 0.4,
    fontSize: 12, bold: true, color: colors.primary
  });
  const actions = compliance.actions_required || ['Implement TCFD recommendations', 'Develop transition plan', 'Enhance assurance'];
  slide8.addText(actions.slice(0, 4).map(a => `• ${a}`).join('\n'), {
    x: 0.6, y: 3.9, w: 8.8, h: 1,
    fontSize: 10, color: colors.darkGray
  });

  // ============== Slide 9: Level Progression - Journey to Next ESG Level ==============
  const levelProgression = data.ai_analysis?.level_progression;
  if (levelProgression) {
    const slideLP = pptx.addSlide();
    
    // Header
    slideLP.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: colors.primary }
    });
    slideLP.addText('Level Up: Journey to Next ESG Maturity Level', {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 24, bold: true, color: colors.white
    });
    slideLP.addText('Time, Effort, Investment & Benefits Analysis', {
      x: 0.5, y: 0.6, w: 9, h: 0.3,
      fontSize: 11, color: 'FFFFFF'
    });

    // Current to Next Level Visual
    const currentLevel = levelProgression.current_level?.level_number || Math.floor(overallScore);
    const nextLevel = levelProgression.next_level?.level_number || Math.min(currentLevel + 1, 5);
    const currentLevelName = levelProgression.current_level?.level_name || data.overall_maturity_level || 'Current';
    const nextLevelName = levelProgression.next_level?.level_name || 'Next Level';
    
    // Current Level Circle
    slideLP.addShape('ellipse', {
      x: 1.5, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.orange }
    });
    slideLP.addText(String(currentLevel), {
      x: 1.5, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Current', {
      x: 1.2, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(currentLevelName, {
      x: 1, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });

    // Arrow with time
    slideLP.addShape('rect', {
      x: 3.2, y: 1.75, w: 3.6, h: 0.3,
      fill: { color: colors.primary }
    });
    slideLP.addText(levelProgression.progression_requirements?.time_estimate || '12-18 months', {
      x: 3.2, y: 2.1, w: 3.6, h: 0.3,
      fontSize: 10, bold: true, color: colors.primary, align: 'center'
    });

    // Next Level Circle
    slideLP.addShape('ellipse', {
      x: 7.3, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.primary }
    });
    slideLP.addText(String(nextLevel), {
      x: 7.3, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Target', {
      x: 7, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(nextLevelName, {
      x: 6.8, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.primary, align: 'center'
    });

    // Resource Requirements Grid
    const reqY = 3.2;
    const reqCards = [
      { label: 'TIME', value: levelProgression.progression_requirements?.time_estimate || '12-18 months', color: '3B82F6' },
      { label: 'MANPOWER', value: levelProgression.progression_requirements?.manpower_resources?.internal_fte || 'Not available', color: '8B5CF6' },
      { label: 'INVESTMENT', value: levelProgression.progression_requirements?.investment_required?.total_range || 'Not available', color: colors.primary },
      { label: 'VALUE', value: levelProgression.progression_requirements?.business_benefits?.quantified_value || 'Not available', color: colors.amber }
    ];

    reqCards.forEach((card, idx) => {
      const x = 0.5 + idx * 2.4;
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 1.2,
        fill: { color: colors.lightGray }
      });
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 0.35,
        fill: { color: card.color }
      });
      slideLP.addText(card.label, {
        x: x, y: reqY + 0.05, w: 2.2, h: 0.25,
        fontSize: 9, bold: true, color: colors.white, align: 'center'
      });
      
      slideLP.addText(card.value, {
        x: x + 0.1, y: reqY + 0.45, w: 2, h: 0.65,
        fontSize: 11, bold: true, color: card.color, align: 'center', valign: 'middle'
      });
    });

    // Business Benefits Section
    const benY = 4.6;
    slideLP.addText('Sustainability Benefits at Next Level', {
      x: 0.5, y: benY, w: 9, h: 0.35,
      fontSize: 12, bold: true, color: colors.primary
    });

    const benefits = [
      { label: 'Carbon Reduction', value: levelProgression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available' },
      { label: 'ESG Rating Improvement', value: levelProgression.progression_requirements?.business_benefits?.revenue_impact || 'Not available' },
      { label: 'Risk Mitigation', value: levelProgression.progression_requirements?.business_benefits?.risk_reduction || 'Not available' }
    ];

    benefits.forEach((ben, idx) => {
      const x = 0.5 + idx * 3.1;
      slideLP.addShape('rect', {
        x: x, y: benY + 0.4, w: 2.9, h: 0.7,
        fill: { color: 'ECFDF5' },
        line: { color: colors.primary, width: 1 }
      });
      slideLP.addText(ben.label, {
        x: x + 0.1, y: benY + 0.45, w: 2.7, h: 0.25,
        fontSize: 8, color: colors.mediumGray
      });
      slideLP.addText(ben.value, {
        x: x + 0.1, y: benY + 0.7, w: 2.7, h: 0.35,
        fontSize: 10, bold: true, color: colors.primary
      });
    });
  }

  // ============== Slide 10 (or 9 if no level progression): Thank You ==============
  const slideThankYou = pptx.addSlide();
  slideThankYou.background = { color: colors.dark };
  
  slideThankYou.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slideThankYou.addText('Thank You', {
    x: 0.5, y: 2, w: 9, h: 1,
    fontSize: 44, bold: true, color: colors.white, align: 'center'
  });
  
  slideThankYou.addText('Your Sustainability Journey Starts Here', {
    x: 0.5, y: 3, w: 9, h: 0.5,
    fontSize: 18, color: colors.secondary, align: 'center'
  });
  
  slideThankYou.addText('www.samanvai.com', {
    x: 0.5, y: 4.3, w: 9, h: 0.5,
    fontSize: 18, color: colors.primary, align: 'center'
  });

  // Download the file
  const companyName = (data.company_name || results.company_name || 'Company').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const fileName = `ESG_Assessment_${companyName}_${new Date().toISOString().split('T')[0]}`;
  appendQualitySlide(pptx, fullResults || results);
  return pptx.writeFile({ fileName: fileName });
};

export default generateSustainabilityPptx;
